import type { MapPoint } from './types';

/**
 * The drawn pin: a 40 px circle centred on the Device's coordinates (Figma `948:567819` — an ellipse
 * 40 × 40 filled `Error/700`, no stroke and no shadow).
 *
 * Exported because three later tickets derive from it rather than restating it: clustering's radius
 * (ticket 08), the fan-out radius for co-located Devices (ticket 09) and the quick view's anchor
 * offset (ticket 11).
 */
export const MARKER_PIN_SIZE_PX = 40;

/**
 * The clickable target, deliberately larger than the drawn pin: 8 px of slop per side, so a near miss
 * at small zoom still hits the Device rather than silently focusing the Boundary beneath
 * (113586 §6c). FE-drafted — no Figma frame draws a hit area.
 *
 * The `.marker` rule in `device-markers.module.scss` carries the same number and rounds it to a circle
 * (`border-radius: 50%`), so the target the browser actually hit-tests is a **28 px radius** about the
 * Device's point. SCSS cannot import TS, so the two sides name each other.
 *
 * **This is the source of truth for ticket 07's click arbitration too.** `MARKER_HIT_RADIUS`
 * (`hit-test.ts`) is to be derived as `MARKER_HIT_SIZE_PX / 2` and imported from here, so
 * `resolveMapClick` answers `device` over exactly the disc the browser hit-tests. In the admin twin the
 * two once disagreed by 8 px — an annulus in which a press landed on this `<button>`, which hit-tests
 * first and swallows the event, while the arbitration would have answered `field`. Deriving one from
 * the other is what closed it; do not re-introduce a second literal.
 */
export const MARKER_HIT_SIZE_PX = 56;

/**
 * Whether a coordinate can be drawn at all.
 *
 * Exported for ticket 05's `boundaries.ts`, which needs the same predicate for a Boundary ring — the
 * admin twin declares it twice and the two copies are free to drift. Import this one instead.
 */
export const isPlottablePoint = ({ lat, lon }: MapPoint) =>
  Number.isFinite(lat) && Number.isFinite(lon) && Math.abs(lat) <= 90 && Math.abs(lon) <= 180;

/**
 * The Devices that can be drawn, in payload order, with everything else about them untouched.
 *
 * An **unplaced device** — one with no valid stored location — never reaches here: ticket 04's contract
 * omits it from `data` entirely and counts it in `meta.unplacedCount`, which is what delivers "no
 * placeholder, no default and no approximate position" (113587 §5a–§5b). What is left for this filter
 * is the coordinate a **schema** cannot reject: `z.number()` happily parses a latitude of 200.
 *
 * What this does **not** do is reject a valid coordinate outside the USA: 113587 §2b is backend
 * placement logic (spec § Out of Scope), and a frontend that silently dropped a plottable Device would
 * be inventing a rule.
 *
 * Nothing is deduplicated: two Devices at identical coordinates both come back (§4a). Ticket 09 fans
 * them out.
 */
export function plottableDevices<T extends { location: MapPoint }>(devices: readonly T[]): T[] {
  return devices.filter((device) => isPlottablePoint(device.location));
}

/**
 * Device markers in the order the keyboard walks them: **name ascending** (113588 §13e, grilling
 * Q14). `label` is the server-resolved display name — the Grower's custom name where one is set,
 * otherwise the actual Device name — so the order matches what the Grower reads on the chip.
 *
 * Tab order needs no roving-tabindex manager: markers are real `<button>`s, so **DOM order is tab
 * order**, and rendering them sorted is the whole implementation. Deterministic, stable under pan
 * and zoom (neither changes a name), and testable without a map.
 *
 * `localeCompare` rather than `<`, so `Barn 2` sorts after `Alpha` and not after `north pump`.
 */
export function inNameOrder<T extends { label: string }>(devices: readonly T[]): T[] {
  return [...devices].sort((a, b) => a.label.localeCompare(b.label));
}
